import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MenuBookRounded, PublicRounded, ArrowBackRounded } from '@mui/icons-material';
import { useTeacherAuth } from '../context/TeacherAuthContext';
import {
  getClassWeeklyPlan,
  saveClassWeeklyPlan,
  getTeacherCurriculum,
  updateTeacherSubjects,
  getCustomSyllabus,
  saveCustomSyllabus,
  extractCurriculumPdf,
  CustomSyllabusChapter,
  CustomSyllabusResponse,
  getSyllabusProgress,
} from '../services/api';
import { SyllabusItem, Teacher, TeacherCurriculumSubject, TeacherSubject } from '../types';
import { resolveTeacherClassContext } from '../utils/teacherClassContext';
import TeacherLayout from './teacher/TeacherLayout';
import SubjectChipsSelect from './common/SubjectChipsSelect';
import SyllabusChecklist from './WeeklyPlan/SyllabusChecklist';
import CoverageBars, { computeNcertCoverage, computeStemCoverage } from './teacher/CurriculumCoverage';
import VoiceTopicCapture from './WeeklyPlan/VoiceTopicCapture';
import HomeworkExamFields, {
  ExamDraft,
  HomeworkDraft,
} from './WeeklyPlan/HomeworkExamFields';

const SUBJECT_LABELS: Record<string, string> = {
  maths: 'Maths',
  science: 'Science',
  social: 'Social',
};

/** Which syllabus source the teacher is looking at. null = not decided yet
 *  (waiting for the NCERT curriculum fetch to pick the default). */
type SourceTab = 'ncert' | 'global';

/** In-progress edit of the Global syllabus. Non-null = the editor is open —
 *  covers all three entry points: PDF-extraction preview (source 'pdf'),
 *  build-by-hand (source 'manual'), and edit-existing (source preserved). */
interface SyllabusDraft {
  name: string;
  source: 'pdf' | 'manual';
  chapters: CustomSyllabusChapter[];
}

/** "One sub-topic per line, or comma-separated" → clean string list. */
const parseSubTopics = (raw: string): string[] =>
  raw
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);

/** Amber nudge for pre-v2 teachers with no subject profile. Saving here only
 *  updates the profile + refetches chapters — it never blocks saving the plan. */
const NoSubjectsPrompt: React.FC<{
  token: string;
  onSaved: (teacher: Teacher) => void;
}> = ({ token, onSaved }) => {
  const [subjects, setSubjects] = useState<TeacherSubject[]>([]);
  const [substitutes, setSubstitutes] = useState<TeacherSubject[]>([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (subjects.length === 0) {
      setErr('Pick at least one subject you teach.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const updated = await updateTeacherSubjects(token, {
        subjects,
        substitute_subjects: substitutes,
      });
      onSaved(updated);
    } catch (e: any) {
      setErr(e?.message || 'Failed to save subjects.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
      <p className="mb-3 text-sm font-medium text-amber-800">
        Which subjects do you teach? This helps us show your chapters
      </p>
      <SubjectChipsSelect
        value={subjects}
        substitutes={substitutes}
        onChange={(next, nextSubs) => {
          setSubjects(next);
          setSubstitutes(nextSubs);
        }}
        disabled={saving}
      />
      {err && <p className="mt-2 text-sm text-red-600">{err}</p>}
      <button
        type="button"
        onClick={save}
        disabled={saving}
        className="mt-3 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
};

const ClassWeeklyPlan: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, teacher, updateTeacher } = useTeacherAuth();
  const { school, grade, section } = resolveTeacherClassContext((location.state as any) || {});

  const [homeworks, setHomeworks] = useState<HomeworkDraft[]>([]);
  const [exams, setExams] = useState<ExamDraft[]>([]);
  /** null = still loading; [] = nothing indexed / fetch failed (custom-only). */
  const [curriculum, setCurriculum] = useState<TeacherCurriculumSubject[] | null>(null);
  /** Chapter title VoiceTopicCapture asked the checklist to expand + scroll to. */
  const [focusChapterTitle, setFocusChapterTitle] = useState<string | undefined>(undefined);
  /** Free-text topic VoiceTopicCapture wants added as a "your own topic". */
  const [pendingCustom, setPendingCustom] = useState<string | undefined>(undefined);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --- Source switch (NCERT vs Global) — in-page only, not persisted. ---
  const [sourceTab, setSourceTab] = useState<SourceTab | null>(null);

  // --- Global (custom) syllabus state machine ---
  // syllabus null + no error   → 'loading'
  // syllabus null + loadError  → 'load-failed' (banner + retry)
  // draft non-null             → 'editor' (PDF preview / manual build / edit)
  // syllabus.chapters === []   → 'empty' (build-your-own card)
  // otherwise                  → 'checklist' (slim header + SyllabusChecklist)
  // `busy` overlays any state while extracting a PDF or saving.
  const [syllabus, setSyllabus] = useState<CustomSyllabusResponse | null>(null);
  const [syllabusLoadError, setSyllabusLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<SyllabusDraft | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /** Extraction / save errors — rose inline banner in the Global tile. */
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [newChapterTitle, setNewChapterTitle] = useState('');
  const [newChapterSubject, setNewChapterSubject] = useState('');
  const [newChapterSubs, setNewChapterSubs] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const gradeNum = typeof grade === 'string' ? Number(grade) : grade;

  useEffect(() => {
    if (!token || gradeNum == null || Number.isNaN(Number(gradeNum))) return;
    setLoading(true);
    setError(null);
    // Curriculum failure must NEVER block the plan — degrade to custom-only.
    // The checklist fetches its own progress; here we only need homework/exam
    // (class-plan) + the curriculum tree.
    Promise.all([
      getClassWeeklyPlan(token, Number(gradeNum), section),
      getTeacherCurriculum(token, Number(gradeNum)).catch(() => ({
        grade: Number(gradeNum),
        subjects: [] as TeacherCurriculumSubject[],
      })),
    ])
      .then(([plan, curr]) => {
        setSavedAt(plan.updated_at || null);
        setHomeworks(
          (plan.homeworks || []).map((h) => ({
            subject: h.subject || '',
            task: h.task || '',
            due_date: h.due_date || '',
          }))
        );
        setExams(
          (plan.exams || []).map((e) => ({
            subject: e.subject || '',
            title: e.title || '',
            date: e.date || '',
          }))
        );
        setCurriculum(curr.subjects || []);
      })
      .catch((e) => {
        setError(e.message || 'Failed to load plan');
        setCurriculum([]);
      })
      .finally(() => setLoading(false));
  }, [token, gradeNum, section]);

  const refetchCurriculum = useCallback(async () => {
    if (!token || gradeNum == null) return;
    try {
      const curr = await getTeacherCurriculum(token, Number(gradeNum));
      setCurriculum(curr.subjects || []);
    } catch {
      setCurriculum((c) => c ?? []);
    }
  }, [token, gradeNum]);

  // Fetch the teacher's own (Global) syllabus for this class.
  const fetchSyllabus = useCallback(async () => {
    if (!token || gradeNum == null || Number.isNaN(Number(gradeNum))) return;
    setSyllabusLoadError(null);
    try {
      const res = await getCustomSyllabus(token, Number(gradeNum), section);
      setSyllabus(res);
    } catch (e: any) {
      setSyllabusLoadError(e?.message || 'Failed to load your syllabus.');
    }
  }, [token, gradeNum, section]);

  useEffect(() => {
    setSyllabus(null);
    setDraft(null);
    setGlobalError(null);
    fetchSyllabus();
  }, [fetchSyllabus]);

  // Class coverage ticks — feeds the per-subject progress bars above each
  // checklist. Refreshed after every tick/untick via onTicksChanged.
  const [progressItems, setProgressItems] = useState<SyllabusItem[] | null>(null);
  const refreshProgress = useCallback(async () => {
    if (!token || gradeNum == null || Number.isNaN(Number(gradeNum))) return;
    try {
      const res = await getSyllabusProgress(token, Number(gradeNum), section);
      setProgressItems(res.items || []);
    } catch {
      setProgressItems((prev) => prev ?? []);
    }
  }, [token, gradeNum, section]);
  useEffect(() => {
    refreshProgress();
  }, [refreshProgress]);

  // Default tile: STEM — it's the curriculum teachers actually work from.
  useEffect(() => {
    if (sourceTab !== null || curriculum === null) return;
    setSourceTab('global');
  }, [curriculum, sourceTab]);

  // Pseudo-subject mapping: the Global syllabus rides through SyllabusChecklist
  // as ONE subject with `subject: null` (chapter_number null) — ticks save with
  // subject null + the chapter title, which the server treats as Global ticks.
  const globalSubjects = useMemo<TeacherCurriculumSubject[]>(() => {
    if (!syllabus || syllabus.chapters.length === 0) return [];
    return [
      {
        subject: null as any,
        label: 'STEM',
        is_substitute: false,
        chapters: syllabus.chapters.map((c) => ({
          title: c.title,
          chapter_number: null as any,
          sub_topics: c.sub_topics,
          // The module the chapter belongs to, so the checklist can group by it.
          // Carried on the CHAPTER; the subject stays null above, which is what
          // the tick key uses — every STEM tick on record has subject null.
          subject: c.subject ?? null,
        })),
      },
    ];
  }, [syllabus]);

  // --- Per-subject class progress (covered / total sub-topics) ---
  const ncertProgress = useMemo(
    () => computeNcertCoverage(curriculum, progressItems),
    [curriculum, progressItems]
  );
  const stemProgress = useMemo(
    () => computeStemCoverage(syllabus, progressItems),
    [syllabus, progressItems]
  );

  // --- Global syllabus actions ---

  const startPdfUpload = () => fileInputRef.current?.click();

  const onPdfSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file later
    if (!file || !token || gradeNum == null) return;
    setBusy('Reading your PDF…');
    setGlobalError(null);
    try {
      const res = await extractCurriculumPdf(token, file, Number(gradeNum), section);
      setDraft({ name: res.name || '', source: 'pdf', chapters: res.chapters || [] });
      setNewChapterTitle('');
      setNewChapterSubs('');
    } catch (err: any) {
      setGlobalError(err?.message || 'Could not read that PDF. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const openManualEditor = () => {
    setGlobalError(null);
    setDraft({ name: syllabus?.name || '', source: 'manual', chapters: [] });
    setNewChapterTitle('');
    setNewChapterSubs('');
  };

  const discardDraft = () => {
    setDraft(null);
    setGlobalError(null);
    setNewChapterTitle('');
    setNewChapterSubject('');
    setNewChapterSubs('');
  };

  const updateDraftChapterTitle = (idx: number, title: string) => {
    setDraft((d) =>
      d
        ? { ...d, chapters: d.chapters.map((c, i) => (i === idx ? { ...c, title } : c)) }
        : d
    );
  };

  const updateDraftChapterSubject = (idx: number, subject: string) => {
    setDraft((d) =>
      d
        ? { ...d, chapters: d.chapters.map((c, i) => (i === idx ? { ...c, subject } : c)) }
        : d
    );
  };

  const removeDraftChapter = (idx: number) => {
    setDraft((d) => (d ? { ...d, chapters: d.chapters.filter((_, i) => i !== idx) } : d));
  };

  const removeDraftSubTopic = (chapterIdx: number, subIdx: number) => {
    setDraft((d) =>
      d
        ? {
            ...d,
            chapters: d.chapters.map((c, i) =>
              i === chapterIdx
                ? { ...c, sub_topics: c.sub_topics.filter((_, j) => j !== subIdx) }
                : c
            ),
          }
        : d
    );
  };

  const addDraftChapter = () => {
    const title = newChapterTitle.trim();
    if (!title) return;
    const subject = newChapterSubject.trim() || null;
    setDraft((d) =>
      d
        ? {
            ...d,
            chapters: [...d.chapters, { title, subject, sub_topics: parseSubTopics(newChapterSubs) }],
          }
        : d
    );
    setNewChapterTitle('');
    setNewChapterSubject('');
    setNewChapterSubs('');
  };

  const saveDraft = async () => {
    if (!token || gradeNum == null || !draft) return;
    if (draft.chapters.length === 0) {
      setGlobalError('Add at least one chapter before saving.');
      return;
    }
    if (draft.chapters.some((c) => !c.title.trim())) {
      setGlobalError('Every chapter needs a title.');
      return;
    }
    setBusy('Saving your syllabus…');
    setGlobalError(null);
    try {
      const res = await saveCustomSyllabus(token, {
        grade: Number(gradeNum),
        section,
        name: draft.name.trim() || undefined,
        source: draft.source,
        chapters: draft.chapters.map((c) => ({
          title: c.title.trim(),
          subject: (c.subject ?? '').trim() || null,
          sub_topics: c.sub_topics,
        })),
      });
      // PUT returns the fresh full state — adopt it and land on the checklist.
      setSyllabus(res);
      setDraft(null);
    } catch (err: any) {
      setGlobalError(err?.message || 'Failed to save your syllabus.');
    } finally {
      setBusy(null);
    }
  };

  const subjectOptions = useMemo(() => {
    if (curriculum && curriculum.length > 0) {
      return curriculum.map((s) => ({ key: s.subject, label: s.label }));
    }
    const all = [...(teacher?.subjects ?? []), ...(teacher?.substitute_subjects ?? [])];
    return all.map((s) => ({ key: s, label: SUBJECT_LABELS[s] || s }));
  }, [curriculum, teacher]);

  const save = useCallback(async () => {
    if (!token || gradeNum == null) return;
    if (exams.some((e) => !e.date)) {
      setError('Pick a date for every exam.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Topics are ticked off in the checklist (auto-saved separately). Here we
      // only save homeworks + exams — topics/mode are omitted (server defaults
      // topics to []). FULL-STATE semantics: we always POST the complete lists;
      // an empty array clears them on the server.
      const plan = await saveClassWeeklyPlan(token, {
        grade: Number(gradeNum),
        section,
        homeworks: homeworks
          .filter((h) => h.task.trim())
          .map((h) => ({
            subject: h.subject || null,
            task: h.task.trim(),
            due_date: h.due_date || null,
          })),
        exams: exams
          .filter((e) => e.date)
          .map((e) => ({
            date: e.date,
            subject: e.subject || null,
            title: e.title.trim() || null,
          })),
      });
      setSavedAt(plan.updated_at || new Date().toISOString());
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [token, gradeNum, section, homeworks, exams]);

  const hasNoSubjects = !!teacher && (teacher.subjects?.length ?? 0) === 0;
  const gradeValid = gradeNum != null && !Number.isNaN(Number(gradeNum));

  // --- New Curriculum-page shell: tabs + top progress cards ---
  const [tab, setTab] = useState<'curriculum' | 'plan'>('curriculum');

  // Progress cards + overview follow the SELECTED source (NCERT vs STEM), so they
  // change when the teacher switches — and Overall matches the subjects shown.
  const allCoverage = useMemo(
    () => (sourceTab === 'global' ? stemProgress : ncertProgress).filter((r) => r.total > 0),
    [sourceTab, ncertProgress, stemProgress]
  );
  const overall = useMemo(() => {
    const covered = allCoverage.reduce((n, r) => n + r.covered, 0);
    const total = allCoverage.reduce((n, r) => n + r.total, 0);
    return { covered, total, pct: total ? Math.round((covered / total) * 100) : 0 };
  }, [allCoverage]);
  const subjectsLabel = useMemo(
    () => (teacher?.subjects ?? []).map((s) => SUBJECT_LABELS[s] || s).join(', '),
    [teacher]
  );

  // --- Global tile renderers ---

  const renderDraftEditor = (d: SyllabusDraft) => (
    <div className="rounded-2xl border border-[#E9E6F4] bg-white p-4 sm:p-5">
      <button
        type="button"
        onClick={discardDraft}
        disabled={!!busy}
        className="mb-3 flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-700 disabled:opacity-50"
      >
        <ArrowBackRounded fontSize="small" /> Back
      </button>
      <h3 className="text-base font-semibold text-slate-900">
        {d.source === 'pdf' ? 'Check what we found' : 'Build your syllabus'}
      </h3>
      <p className="mt-0.5 text-xs text-slate-500">
        {d.source === 'pdf'
          ? 'From your PDF — fix anything we misread, then save.'
          : 'Add your chapters one by one, then save.'}
      </p>

      <label className="mt-4 block text-xs font-medium text-slate-500">Syllabus name</label>
      <input
        type="text"
        value={d.name}
        onChange={(e) => setDraft((prev) => (prev ? { ...prev, name: e.target.value } : prev))}
        placeholder="My curriculum"
        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-violet-400 focus:outline-none"
      />

      {d.chapters.length > 0 && (
        <div className="mt-4 space-y-2">
          {d.chapters.map((ch, i) => (
            <div key={i} className="rounded-xl border border-[#E9E6F4] bg-white p-3">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={ch.title}
                  onChange={(e) => updateDraftChapterTitle(i, e.target.value)}
                  aria-label={`Chapter ${i + 1} title`}
                  placeholder="Chapter title"
                  className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-800 focus:border-violet-400 focus:outline-none"
                />
                <input
                  type="text"
                  value={ch.subject ?? ''}
                  onChange={(e) => updateDraftChapterSubject(i, e.target.value)}
                  aria-label={`Chapter ${i + 1} subject`}
                  placeholder="Subject"
                  className="w-28 flex-none rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-violet-400 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => removeDraftChapter(i)}
                  aria-label={`Remove chapter ${ch.title || i + 1}`}
                  className="flex-shrink-0 rounded-full px-2 py-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                >
                  ×
                </button>
              </div>
              {ch.sub_topics.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {ch.sub_topics.map((st, j) => (
                    <span
                      key={`${st}-${j}`}
                      className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2.5 py-1 text-xs text-violet-800"
                    >
                      {st}
                      <button
                        type="button"
                        onClick={() => removeDraftSubTopic(i, j)}
                        aria-label={`Remove sub-topic ${st}`}
                        className="text-violet-400 hover:text-violet-700"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-3">
        <label className="mb-1 block text-xs font-medium text-slate-500">+ Add a chapter</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={newChapterTitle}
            onChange={(e) => setNewChapterTitle(e.target.value)}
            placeholder="Chapter title"
            className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
          />
          <input
            type="text"
            value={newChapterSubject}
            onChange={(e) => setNewChapterSubject(e.target.value)}
            placeholder="Subject"
            aria-label="New chapter subject"
            className="w-28 flex-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
          />
        </div>
        <textarea
          value={newChapterSubs}
          onChange={(e) => setNewChapterSubs(e.target.value)}
          rows={2}
          placeholder="One sub-topic per line, or comma-separated"
          className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none"
        />
        <button
          type="button"
          onClick={addDraftChapter}
          disabled={!newChapterTitle.trim() || !!busy}
          className="mt-2 rounded-lg border border-violet-200 bg-white px-4 py-2 text-sm font-medium text-violet-700 hover:border-violet-400 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Add chapter
        </button>
      </div>

      <div className="mt-4 flex items-center gap-4">
        <button
          type="button"
          onClick={saveDraft}
          disabled={!!busy || d.chapters.length === 0}
          className="rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Save syllabus'}
        </button>
        <button
          type="button"
          onClick={discardDraft}
          disabled={!!busy}
          className="text-sm font-medium text-slate-500 hover:text-slate-700 disabled:opacity-50"
        >
          Discard
        </button>
      </div>
    </div>
  );

  const renderGlobalContent = () => {
    // load-failed → rose banner + retry (never an infinite spinner).
    if (syllabus === null && syllabusLoadError) {
      return (
        <div className="rounded-xl bg-rose-50 p-3 text-sm text-rose-700">
          {syllabusLoadError}{' '}
          <button type="button" onClick={fetchSyllabus} className="font-medium underline">
            Retry
          </button>
        </div>
      );
    }

    // loading → small skeleton.
    if (syllabus === null) {
      return (
        <div className="animate-pulse space-y-2" aria-label="Loading your syllabus">
          <div className="h-10 rounded-xl bg-slate-100" />
          <div className="h-10 w-3/4 rounded-xl bg-slate-100" />
        </div>
      );
    }

    // editor (PDF preview / manual build / edit-existing).
    if (draft) return renderDraftEditor(draft);

    // empty → build-your-own card with the two paths.
    if (syllabus.chapters.length === 0) {
      return (
        <div className="rounded-2xl border border-[#E9E6F4] bg-white p-5">
          <h3 className="text-base font-semibold text-slate-900">Build your own syllabus</h3>
          <p className="mt-1 text-sm text-slate-500">
            Upload your school's curriculum PDF and we'll pull the chapters out of it — or add
            them by hand.
          </p>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={startPdfUpload}
              disabled={!!busy}
              className="rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Upload curriculum PDF
            </button>
            <button
              type="button"
              onClick={openManualEditor}
              disabled={!!busy}
              className="rounded-xl border border-[#E9E6F4] bg-white px-4 py-2.5 text-sm font-medium text-violet-700 hover:border-violet-300 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Add manually
            </button>
          </div>
        </div>
      );
    }

    // checklist → slim header row + the shared checklist via the pseudo-subject.
    return (
      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-800">
              {syllabus.name || 'My curriculum'}
            </p>
            {syllabus.updated_at && (
              <p className="text-xs text-slate-400">
                Updated {new Date(syllabus.updated_at).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
        <p className="mb-2 text-xs text-slate-500">
          Tap the sub-topics you've covered, then press “Add” to save them.
        </p>
        {token && gradeValid && (
          <>
            <CoverageBars rows={stemProgress} />
            <SyllabusChecklist
              subjects={globalSubjects}
              grade={Number(gradeNum)}
              section={section}
              token={token}
              focusChapterTitle={focusChapterTitle}
              pendingCustom={pendingCustom}
              onCustomConsumed={() => setPendingCustom(undefined)}
              onTicksChanged={refreshProgress}
            />
          </>
        )}
      </div>
    );
  };

  return (
    <TeacherLayout active="plan">
    <div className="mx-auto max-w-6xl pb-4">
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Curriculum</h1>
        <p className="mt-0.5 text-sm text-slate-500">
          {school} · Grade {grade}
          {section ? ` · Section ${section}` : ''}
          {subjectsLabel ? <span className="font-semibold text-violet-700"> · {subjectsLabel}</span> : null}
        </p>
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-red-700 text-sm">{error}</div>}

      {hasNoSubjects && token && (
        <NoSubjectsPrompt
          token={token}
          onSaved={(updated) => {
            updateTeacher(updated);
            refetchCurriculum();
          }}
        />
      )}

      {/* Progress cards */}
      {allCoverage.length > 0 && (
        <div className={`mb-5 grid grid-cols-1 gap-3 ${sourceTab !== 'global' ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:max-w-sm'}`}>
          <div className="flex items-center gap-4 rounded-2xl border border-[#E9E6F4] bg-white p-4">
            <ProgressDonut pct={overall.pct} />
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Overall progress</p>
              <p className="mt-1 text-lg font-bold text-slate-900">{overall.covered}/{overall.total}</p>
              <p className="text-xs text-slate-500">Topics covered</p>
            </div>
          </div>
          {sourceTab !== 'global' && allCoverage.slice(0, 3).map((r) => {
            const pct = r.total ? Math.min(100, Math.round((r.covered / r.total) * 100)) : 0;
            return (
              <div key={r.label} className="rounded-2xl border border-[#E9E6F4] bg-white p-4">
                <div className="flex items-center justify-between">
                  <p className="truncate text-sm font-semibold text-slate-800">{r.label}</p>
                  <span className="text-sm font-bold text-slate-900">{pct}%</span>
                </div>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
                  <div className={`h-full rounded-full ${pct >= 100 ? 'bg-emerald-500' : 'bg-violet-600'}`} style={{ width: `${pct}%` }} />
                </div>
                <p className="mt-2 text-xs text-slate-500">{r.covered} / {r.total} topics</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Tabs */}
      <div className="mb-5 flex gap-6 border-b border-[#E9E6F4]">
        {([['curriculum', 'Curriculum'], ['plan', 'Homework & Exams']] as const).map(([k, l]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-1 pb-3 text-sm font-semibold transition-colors ${
              tab === k ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {/* Hidden PDF input — shared by "Upload curriculum PDF" + "Replace from PDF". */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,application/pdf"
        onChange={onPdfSelected}
        className="hidden"
        aria-hidden
      />

      {tab === 'curriculum' && (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
          {/* Main column */}
          <div className="min-w-0">
            <p className="mb-4 text-sm text-slate-600">
              Tick off what your class has covered — from NCERT or your syllabus. When a student
              opens the app, the curiosity greets them with it — then follows wherever they take the
              conversation.
            </p>

            {gradeValid && (
              <div className="mb-4">
                <VoiceTopicCapture
                  grade={Number(gradeNum)}
                  section={section}
                  onFocusChapter={(title) => setFocusChapterTitle(title)}
                  onAddCustom={(text) => setPendingCustom(text)}
                />
              </div>
            )}

            {/* Source switch: NCERT (standard) vs Global (the teacher's own syllabus). */}
            {gradeValid && (
              <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setSourceTab('ncert')}
                  aria-pressed={sourceTab === 'ncert'}
                  className={`flex items-start gap-3 rounded-2xl bg-white p-4 text-left transition-colors ${
                    sourceTab === 'ncert'
                      ? 'border-2 border-violet-600 p-[15px]'
                      : 'border border-[#E9E6F4] hover:border-violet-200'
                  }`}
                >
                  <MenuBookRounded fontSize="small" className="mt-0.5 flex-shrink-0 text-violet-600" />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-slate-900">NCERT</span>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      Standard syllabus for Grade {grade}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setSourceTab('global')}
                  aria-pressed={sourceTab === 'global'}
                  className={`flex items-start gap-3 rounded-2xl bg-white p-4 text-left transition-colors ${
                    sourceTab === 'global'
                      ? 'border-2 border-violet-600 p-[15px]'
                      : 'border border-[#E9E6F4] hover:border-violet-200'
                  }`}
                >
                  <PublicRounded fontSize="small" className="mt-0.5 flex-shrink-0 text-violet-600" />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-slate-900">STEM</span>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      Your own curriculum — from a PDF or built by hand
                    </span>
                  </span>
                </button>
              </div>
            )}

            {sourceTab === 'ncert' && (
              <div>
                <label className="block text-sm font-medium text-slate-700">Pick from the syllabus</label>
                <p className="mb-2 mt-0.5 text-xs text-slate-500">
                  Tap the sub-topics you've covered, then press “Add” to save them.
                </p>
                {curriculum === null || loading ? (
                  <p className="text-sm text-slate-400">Loading your chapters…</p>
                ) : (
                  gradeValid &&
                  token && (
                    <>
                      <CoverageBars rows={ncertProgress} />
                      <SyllabusChecklist
                        subjects={curriculum}
                        grade={Number(gradeNum)}
                        section={section}
                        token={token}
                        focusChapterTitle={focusChapterTitle}
                        pendingCustom={pendingCustom}
                        onCustomConsumed={() => setPendingCustom(undefined)}
                        onTicksChanged={refreshProgress}
                      />
                    </>
                  )
                )}
              </div>
            )}

            {sourceTab === 'global' && (
              <div>
                {busy && (
                  <div className="mb-3 flex items-center gap-2 text-sm text-slate-500">
                    <span
                      aria-hidden
                      className="h-4 w-4 flex-shrink-0 animate-spin rounded-full border-2 border-violet-200 border-t-violet-600"
                    />
                    {busy}
                  </div>
                )}
                {globalError && (
                  <div className="mb-3 rounded-xl bg-rose-50 p-3 text-sm text-rose-700">{globalError}</div>
                )}
                {renderGlobalContent()}
              </div>
            )}

            {sourceTab === null && <p className="text-sm text-slate-400">Loading your chapters…</p>}
          </div>

          {/* Right sidebar */}
          <aside className="flex flex-col gap-4">
            <div className="rounded-2xl border border-[#E9E6F4] bg-white p-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Quick actions</p>
              <div className="flex flex-col gap-0.5">
                <QuickAction icon="📚" label="Add homework" onClick={() => setTab('plan')} />
                <QuickAction icon="📝" label="Add exam" onClick={() => setTab('plan')} />
                <QuickAction icon="📄" label="Import from PDF" onClick={startPdfUpload} />
                <QuickAction
                  icon="🧪"
                  label="Create assessments"
                  onClick={() => navigate('/teacher-assessments', { state: location.state })}
                />
              </div>
            </div>
            <div className="rounded-2xl border border-[#E9E6F4] bg-white p-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Class overview</p>
              <OverviewRow k="Total topics" v={String(overall.total)} />
              <OverviewRow k="Completed" v={String(overall.covered)} />
              <OverviewRow k="Not started" v={String(Math.max(0, overall.total - overall.covered))} />
              {savedAt ? <OverviewRow k="Last updated" v={new Date(savedAt).toLocaleDateString()} /> : null}
            </div>
          </aside>
        </div>
      )}

      {tab === 'plan' && (
        <div className="max-w-3xl">
          <HomeworkExamFields
            homeworks={homeworks}
            exams={exams}
            onHomeworksChange={setHomeworks}
            onExamsChange={setExams}
            subjectOptions={subjectOptions}
          />
        </div>
      )}

      {/* Sticky save bar — homeworks + exams only. */}
      <div className="sticky bottom-0 z-10 -mx-4 mt-6 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <div className="flex items-center gap-3">
          <button
            onClick={save}
            disabled={saving || loading}
            className={`flex-1 rounded-xl px-5 py-3 text-sm font-medium text-white disabled:opacity-50 sm:flex-none sm:px-8 ${
              saved ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save homework & exams'}
          </button>
          {savedAt && !saving && (
            <span className="text-xs text-slate-400">Last saved {new Date(savedAt).toLocaleString()}</span>
          )}
        </div>
      </div>
    </div>
    </TeacherLayout>
  );
};

const ProgressDonut: React.FC<{ pct: number }> = ({ pct }) => {
  const r = 26;
  const c = 2 * Math.PI * r;
  const filled = (Math.max(0, Math.min(100, pct)) / 100) * c;
  return (
    <svg viewBox="0 0 64 64" className="h-16 w-16 flex-none">
      <circle cx="32" cy="32" r={r} fill="none" stroke="#eef1f6" strokeWidth="8" />
      <circle
        cx="32"
        cy="32"
        r={r}
        fill="none"
        stroke="#7c3aed"
        strokeWidth="8"
        strokeLinecap="round"
        strokeDasharray={`${filled} ${c - filled}`}
        transform="rotate(-90 32 32)"
      />
      <text x="32" y="37" textAnchor="middle" fontSize="15" fontWeight="700" fill="#0f172a">
        {pct}%
      </text>
    </svg>
  );
};

const QuickAction: React.FC<{ icon: string; label: string; onClick: () => void }> = ({ icon, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-2.5 rounded-lg px-2 py-2 text-left text-sm font-medium text-slate-700 hover:bg-violet-50"
  >
    <span className="text-base">{icon}</span> {label}
  </button>
);

const OverviewRow: React.FC<{ k: string; v: string }> = ({ k, v }) => (
  <div className="flex items-center justify-between border-b border-[#F1EFFA] py-2 text-sm last:border-0">
    <span className="text-slate-500">{k}</span>
    <span className="font-bold text-slate-900">{v}</span>
  </div>
);

export default ClassWeeklyPlan;
